import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';

import {
  IFilterField,
  IStatic,
  ITextSearch,
} from '../../common-types/company-bookings';
import { AppThunk, Toaster, CallbackFunction } from '@share/utils';
import { CompanyBookingsAPI } from '@share/api';
import { BOOKING_ID_MATCHEDBY, LEAD_NAME_MATCHEDBY, PROPERTY_NAME_MATCHEDBY } from '@share/constants';
import { ICompanyBooking, ICompanyBookingsFilters, ICompanyBookingsParams } from '../../common-types/company-bookings';

export interface ICompanyBookingsState {
  loading: boolean;
  error: string;
  result: {
    bookings: ICompanyBooking[];
    totalOrderCount: number;
    staticData: IStatic;
  },
  pagination: TablePaginationConfig;
  sort: SorterResult<ICompanyBooking>;
  filters: ICompanyBookingsFilters;
  textSearch: ITextSearch;
  textSearchLabel: string;
}

const one = 1;
const ten = 10;

const InitialFiltersCompanyBookings: ICompanyBookingsFilters = {
  bookingId: { value: null },
  propertyName: { value: null },
  leadName: { value: null },
  bookingType: { value: null },
}

const initialState: ICompanyBookingsState = {
  loading: true,
  error: '',
  result: {
    bookings: [],
    totalOrderCount: null,
    staticData: null
  },
  pagination: {
    current: one,
    pageSize: ten,
  },
  sort: null,
  textSearch: null,
  textSearchLabel: '',
  filters: { ...InitialFiltersCompanyBookings }
};

const processFilter = (state: ICompanyBookingsState, field: keyof ICompanyBookingsFilters, payload: any) => {
  state.filters[field] = payload;
}

const companyBookingsSlice = createSlice({
  name: 'companyBookings',
  initialState,
  reducers: {
      setFilters: (state: ICompanyBookingsState, { payload }: PayloadAction<ICompanyBookingsFilters>) => {
      state.filters = payload;
    },

    setBookingId: (
      state: ICompanyBookingsState,
      { payload }: PayloadAction<IFilterField<number>>) => {
      processFilter(state, 'bookingId', payload);
    },
    setPropertyName: (
      state: ICompanyBookingsState,
      { payload }: PayloadAction<IFilterField<string>>) => {
      processFilter(state, 'propertyName', payload);
    },
    setBookingType: (
      state: ICompanyBookingsState,
      { payload }: PayloadAction<IFilterField<string>>) => {
      processFilter(state, 'bookingType', payload);
    },
    setLeadName: (
      state: ICompanyBookingsState,
      { payload }: PayloadAction<IFilterField<string>>) => {
      processFilter(state, 'leadName', payload);
    },
    
    resetFilters: (state: ICompanyBookingsState) => {
      state.filters.bookingId = { value: null };
      state.filters.propertyName = { value: null };
      state.filters.leadName = { value: null };
      state.filters.bookingType = { value: null };
    
      state.textSearch = null;
      state.textSearchLabel = '';
    },

    setSort: (
      state: ICompanyBookingsState,
      { payload }: PayloadAction<SorterResult<ICompanyBooking>>,
    ) => {
      state.sort = payload;
    },
    setLoading: (state: ICompanyBookingsState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setTextSearch: (state: ICompanyBookingsState, { payload }: PayloadAction<ITextSearch>) => {
      state.textSearch = payload;
    },
    setTextSearchLabel: (state: ICompanyBookingsState, { payload }: PayloadAction<string>) => {
      state.textSearchLabel = payload;
    },
    setError: (state: ICompanyBookingsState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setCompanyBookings: (state: ICompanyBookingsState, { payload }: PayloadAction<ICompanyBooking[]>) => {
      state.result.bookings = payload;
    },
    setStaticData: (state: ICompanyBookingsState, { payload }: PayloadAction<IStatic>) => {
      state.result.staticData = payload;
    },
    setTotalOrderCount: (state: ICompanyBookingsState, { payload }: PayloadAction<number>) => {
      state.result.totalOrderCount = payload;
    },
    setPagination: (
      state: ICompanyBookingsState,
      { payload }: PayloadAction<TablePaginationConfig>) => {
      state.pagination = payload;
    },
  },
});

export const companyBookingsActions = companyBookingsSlice.actions;

export const companyBookingsReducer = companyBookingsSlice.reducer;

export const getCompanyBookings = (bookStatus?: string, exluceErrorMessage?: boolean): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(companyBookingsActions.setLoading(true));
    dispatch(companyBookingsActions.setCompanyBookings([]));
    dispatch(companyBookingsActions.setTotalOrderCount(null));

    try {
      const { companyBookingsStore } = getState();
      const { pagination } = companyBookingsStore;
      
      const params: ICompanyBookingsParams = getCompanyBookingParams(companyBookingsStore, bookStatus);

      params.page = pagination.current;
      params.pageSize = pagination.pageSize;

      const { data } = await CompanyBookingsAPI.getCompanyBookings(params);

      dispatch(companyBookingsActions.setLoading(false));

      if (!data.status) {
        dispatch(companyBookingsActions.setCompanyBookings(data?.items));
        dispatch(companyBookingsActions.setTotalOrderCount(data?.totalResults));
      } else {
        dispatch(companyBookingsActions.setError(`[${data.status}] ${data.title}: ${data.detail}`));

        if (!exluceErrorMessage) {
          Toaster.error(`An error occurred. [${data.status}] ${data.title}: ${data.detail}. If the problem persists, please contact the system administrator.`);
        }
      }
    } catch (error) {
      console.error(error);
      dispatch(companyBookingsActions.setError(error.toString()));
      dispatch(companyBookingsActions.setLoading(false));

      if (!exluceErrorMessage) {
        Toaster.error(`An error occurred: ${error.toString()}. If the problem persists, please contact the system administrator.`);
      }
    }
  };
};

export const getCompanyBookingsExport = (bookStatus?: string, exluceErrorMessage?: boolean): AppThunk => {
  // return async (dispatch, getState) => {

  //   try {
  //     const { companyBookingsStore } = getState();
  //     const { pagination } = companyBookingsStore;
      
  //     const params: IReservationsParams = getCompanyBookingParams(companyBookingsStore, bookStatus);

  //     params.page = pagination.current;
  //     params.pageSize = pagination.pageSize;

  //     await CompanyBookingsAPI.getCompanyBookingsExport(params);
  //   } catch (error) {
  //     console.error(error);
  //     dispatch(companyBookingsActions.setError(error.toString()));

  //     if (!exluceErrorMessage) {
  //       Toaster.error(`An error occurred: ${error.toString()}. If the problem persists, please contact the system administrator.`);
  //     }
  //   }
  // };
};

export const getCompanyBookingStatic = (id: number): AppThunk => {
  // return async (dispatch, getState) => {
  //   try {
  //     dispatch(companyBookingsActions.setStaticData(null));
  //     const { data } = await CompanyBookingsAPI.getStatic(id);

  //   } catch (error) {
  //     console.error(error);
  //     dispatch(companyBookingsActions.setError(error.toString()));
  //     Toaster.error(`An error occurred: ${error.toString()}. If the problem persists, please contact the system administrator.`);
  //   }
  // };
};

export const cancelCompanyBooking = (bookingId: string, comment: string, callback: CallbackFunction): AppThunk => {
  // return async (dispatch) => {
  //   dispatch(companyBookingsActions.setLoading(true));

  //   try {
  //     await CompanyBookingsAPI.cancelBookingAsync(bookingId, comment);

  //     Toaster.success(`Booking cancelled successfully`);

  //     dispatch(getCompanyBookings(null, true));

  //     callback();
  //   } catch (error) {
  //     console.error(error);
  //     dispatch(companyBookingsActions.setError(error.toString()));
  //     dispatch(companyBookingsActions.setLoading(false));

  //     Toaster.error(`An error occurred. If the problem persists, please contact the system administrator.`);
  //   }
  // }
}


export const voucherCompanyBooking = (bookingId: string, recepientEmail: string, additionalEmail: string, callback: CallbackFunction): AppThunk => {
  // return async (dispatch) => {
  //   dispatch(companyBookingsActions.setLoading(true));

  //   try {
  //     await CompanyBookingsAPI.voucherBookingAsync(bookingId, recepientEmail, additionalEmail);

  //     Toaster.success(`Voucher sent successfully`);

  //     dispatch(getCompanyBookings(null, true));

  //     callback();
  //   } catch (error) {
  //     console.error(error);
  //     dispatch(companyBookingsActions.setError(error.toString()));
  //     dispatch(companyBookingsActions.setLoading(false));

  //     Toaster.error(`An error occurred. If the problem persists, please contact the system administrator.`);
  //   }
  // }
}

export const downloadCompanyBookingVoucher = (bookingId: string): AppThunk => {
  // return async (dispatch) => {
  //   try {
  //     await CompanyBookingsAPI.downloadbookingVoucherAsync(bookingId);
  //   } catch (error) {
  //     console.error(error);
  //     dispatch(companyBookingsActions.setError(error.toString()));
  //     dispatch(companyBookingsActions.setLoading(false));

  //     Toaster.error(`An error occurred. If the problem persists, please contact the system administrator.`);
  //   }
  // };
};

const getCompanyBookingParams = (companyBookingsStore: ICompanyBookingsState, status: string) => {
  const { sort, filters, textSearch } = companyBookingsStore;
  
  const params: ICompanyBookingsParams = {} as ICompanyBookingsParams;

  if (filters) {
    if (filters.bookingId.value) {
      params.bookingId = filters.bookingId.value;
    }
    if (filters.propertyName.value) {
      params.propertyName = filters.propertyName.value;
    }
    if (filters.leadName.value) {
      params.leadName = filters.leadName.value;
    }
    if (filters.bookingType.value) {
      params.bookingType = filters.bookingType.value;
    }
  }

  params.status = 'active';
  if (status)
    params.status = status;

  if (textSearch) {
    if (textSearch.matchedBy === BOOKING_ID_MATCHEDBY) {
      params.bookingId = parseInt(textSearch.value); 
    }
    if (textSearch.matchedBy === PROPERTY_NAME_MATCHEDBY) {
      params.propertyName = textSearch.value; 
    }
    if (textSearch.matchedBy === LEAD_NAME_MATCHEDBY) {
      params.leadName = textSearch.value; 
    }
  }

  if (sort) {
    const field = sort.field as string;
    params.orderBy = field;
    params.sortOrder = sort.order === 'descend' ? 'desc' : 'asc';
  }
  return params;
}